import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  CircularProgress
} from '@material-ui/core'

const BidItemPromotionModal = ({
  open,
  onClose,
  onPromoted,
  packageId,
  projectId,
  url,
  preSelectedIds,
  buildings
}) => {
  const [items, setItems] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [building, setBuilding] = useState(buildings && buildings.length > 0 ? '' : 'Main')
  const [section, setSection] = useState('')
  const [promoting, setPromoting] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !packageId) return
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/items`)
      .then(res => {
        // Filter to promotable items (not already promoted, standard/lump_sum/allowance types)
        const promotable = (res.data || []).filter(item =>
          item.promotionStatus !== 'promoted' &&
          ['standard', 'lump_sum', 'allowance'].includes(item.itemType)
        )
        setItems(promotable)
        // Pre-select items that were selected in ItemsTab, or all items
        if (preSelectedIds && preSelectedIds.length > 0) {
          const preSet = new Set(preSelectedIds)
          setSelected(new Set(promotable.filter(i => preSet.has(i._id)).map(i => i._id)))
        } else {
          setSelected(new Set(promotable.map(i => i._id)))
        }
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to load items')
        setLoading(false)
      })
  }, [open, packageId, url]) // eslint-disable-line

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(new Set(items.map(i => i._id)))
    } else {
      setSelected(new Set())
    }
  }

  const handleToggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handlePromote = async () => {
    if (!building || selected.size === 0) return
    setPromoting(true)
    setError('')
    try {
      const res = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/line-items/promote`,
        {
          itemIds: Array.from(selected),
          building,
          section: section || undefined
        }
      )
      if (onPromoted) onPromoted(res.data.promoted)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Promotion failed')
    } finally {
      setPromoting(false)
    }
  }

  const handleClose = () => {
    if (!promoting) {
      setItems([])
      setSelected(new Set())
      setBuilding('')
      setSection('')
      setError('')
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Promote Bid Items to Line Items</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" style={{ marginBottom: 12 }}>{error}</Typography>
        )}

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          {buildings && buildings.length > 0 ? (
            <FormControl size="small" variant="outlined" style={{ minWidth: 200 }}>
              <InputLabel>Building</InputLabel>
              <Select
                value={building}
                onChange={e => setBuilding(e.target.value)}
                label="Building"
              >
                {buildings.map(b => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              variant="outlined"
              label="Building"
              value={building}
              onChange={e => setBuilding(e.target.value)}
              style={{ minWidth: 200 }}
              placeholder="e.g. Main"
            />
          )}
          <TextField
            size="small"
            variant="outlined"
            label="Section"
            value={section}
            onChange={e => setSection(e.target.value)}
            style={{ minWidth: 200 }}
          />
        </div>

        <Typography variant="body2" style={{ marginBottom: 8 }}>
          {selected.size} of {items.length} promotable items selected
        </Typography>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <CircularProgress />
          </div>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="textSecondary" style={{ padding: 20, textAlign: 'center' }}>
            No promotable items found. Items may already be promoted.
          </Typography>
        ) : (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.size > 0 && selected.size < items.length}
                      checked={items.length > 0 && selected.size === items.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => (
                  <TableRow key={item._id} hover selected={selected.has(item._id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(item._id)}
                        onChange={() => handleToggle(item._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {item.bidGroup?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                        {item.itemNo || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.descriptionNormalized || item.descriptionRaw || '—'}</TableCell>
                    <TableCell>{item.unitNormalized || item.unitRaw || '—'}</TableCell>
                    <TableCell align="right">{item.quantity ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={promoting}>Cancel</Button>
        <Button
          onClick={handlePromote}
          color="primary"
          variant="contained"
          disabled={promoting || selected.size === 0 || !building}
        >
          {promoting ? <CircularProgress size={20} /> : `Promote ${selected.size} Items`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BidItemPromotionModal
